import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Check, ChevronDown, UserCog, X, AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Multi-select picker for the "Assigned Team" of Gurus on an appointment.
 * Renders selected Guru chips + a popover with search + checkbox list of
 * available Gurus (fetched once from /api/cleaners).
 *
 * Controlled component: value is an array of user_id strings; onChange
 * receives the next array. Order preserved — first item is treated as the
 * primary Guru by the backend/legacy assigned_cleaner_user_id sync.
 */
const MultiGuruSelect = ({
  value = [], onChange, headers, disabled = false,
  placeholder = 'Assign gurus…', testIdPrefix = 'multi-guru',
  gurusOverride, // optional pre-fetched list to avoid duplicate loads
}) => {
  const [gurus, setGurus] = useState(gurusOverride || []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (gurusOverride) { setGurus(gurusOverride); return; }
    axios.get(`${API_URL}/api/cleaners`, { headers })
      .then(r => setGurus(r.data || []))
      .catch(() => setGurus([]));
  }, [headers, gurusOverride]);

  const byId = useMemo(() => {
    const m = {};
    gurus.forEach(g => { m[g.user_id] = g; });
    return m;
  }, [gurus]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = gurus.filter(g => g.is_available !== false);
    if (!q) return available;
    return available.filter(g => (g.user_name || g.display_name || '').toLowerCase().includes(q));
  }, [gurus, query]);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter(v => v !== id));
    else onChange([...value, id]);
  };

  const removeChip = (e, id) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="w-full min-h-[38px] px-3 py-1.5 rounded-md border border-zinc-200 bg-white text-left flex items-center gap-1.5 flex-wrap hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#F56F05]/30 disabled:opacity-50"
          data-testid={`${testIdPrefix}-trigger`}
        >
          {value.length === 0 ? (
            <span className="text-sm text-zinc-400 flex items-center gap-1.5">
              <UserCog className="h-3.5 w-3.5" /> {placeholder}
            </span>
          ) : (
            value.map((id, idx) => {
              const g = byId[id];
              const name = g?.user_name || g?.display_name || 'Unknown';
              return (
                <span
                  key={id}
                  className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium ${
                    idx === 0 ? 'bg-[#F56F05] text-white' : 'bg-zinc-100 text-zinc-700'
                  }`}
                  data-testid={`${testIdPrefix}-chip-${id}`}
                >
                  {idx === 0 && <span className="text-[9px] font-bold opacity-80">LEAD</span>}
                  {name}
                  <button
                    type="button"
                    onClick={(e) => removeChip(e, id)}
                    className={`ml-0.5 rounded-full hover:bg-black/10 p-0.5 ${idx === 0 ? 'text-white' : 'text-zinc-500'}`}
                    aria-label={`Remove ${name}`}
                    data-testid={`${testIdPrefix}-chip-remove-${id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })
          )}
          <ChevronDown className="h-4 w-4 text-zinc-400 ml-auto flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0"
        align="start"
        data-testid={`${testIdPrefix}-popover`}
      >
        <div className="p-2 border-b border-zinc-100">
          <Input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search gurus…"
            className="h-8 text-sm"
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-xs text-zinc-400 italic px-3 py-6 text-center">
              {query ? 'No gurus match that search.' : 'No available gurus.'}
            </p>
          ) : (
            filtered.map(g => {
              const selected = value.includes(g.user_id);
              const idx = value.indexOf(g.user_id);
              const eligible = g.is_eligible_for_assignment !== false;
              const missing = [];
              if (!g.photo_url) missing.push('photo');
              if (!(g.qualified_services || []).length) missing.push('qualified services');
              return (
                <button
                  key={g.user_id}
                  type="button"
                  onClick={() => toggle(g.user_id)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-orange-50 text-left transition-colors"
                  data-testid={`${testIdPrefix}-option-${g.user_id}`}
                  title={!eligible ? `Missing: ${missing.join(', ')}` : undefined}
                >
                  <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                    selected ? 'bg-[#F56F05] border-[#F56F05]' : 'border border-zinc-300'
                  }`}>
                    {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </div>
                  <span className="text-sm text-zinc-900 flex-1 truncate">{g.user_name || g.display_name}</span>
                  {!eligible && (
                    <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid={`${testIdPrefix}-warn-${g.user_id}`}>
                      <AlertTriangle className="h-2.5 w-2.5" /> Setup
                    </span>
                  )}
                  {selected && idx === 0 && (
                    <span className="text-[9px] font-bold text-[#F56F05] bg-orange-50 px-1.5 py-0.5 rounded">LEAD</span>
                  )}
                </button>
              );
            })
          )}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t border-zinc-100 flex items-center justify-between">
            <span className="text-[11px] text-zinc-500">
              {value.length} guru{value.length !== 1 ? 's' : ''} on team
            </span>
            <Button
              size="sm" variant="ghost"
              className="h-6 text-[11px] text-zinc-500 hover:text-red-600"
              onClick={() => onChange([])}
              data-testid={`${testIdPrefix}-clear`}
            >
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MultiGuruSelect;
